import { useMemo } from 'react';
import { Image, ImageStyle } from 'expo-image';

import { ImageProps } from './Image.types';
import useStyle from '../../hooks/useStyle';
import { getImages } from '../../assets/images/registry';

import '../../assets/images';

// Suggested fix for expo-image
// See this crash on Sentry "Unexpected exception thrown by non-Glide code"
function deferNextFrame<T extends (...args: never[]) => void>(
  handler?: T,
): (...args: Parameters<T>) => void {
  return (...args: Parameters<T>) => {
    if (!handler) {
      return;
    }

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => handler(...args));
      return;
    }

    setTimeout(() => handler(...args), 0);
  };
}

const maybeDefer = <T extends (...args: never[]) => void>(handler?: T) =>
  handler ? deferNextFrame(handler) : undefined;

function CustomImage({
  name,
  source,
  placeholder,
  contentFit = 'contain',
  placeholderContentFit,
  contentPosition,
  transition,
  blurRadius,
  tintColor,
  priority,
  cachePolicy,
  responsivePolicy,
  recyclingKey,
  autoplay,
  onLoadStart,
  onLoad,
  onProgress,
  onError,
  onLoadEnd,
  onDisplay,
  focusable,
  accessible,
  accessibilityLabel,
  alt,
  enableLiveTextInteraction,
  allowDownscaling,
  decodeFormat,
  testID,
  ...rest
}: ImageProps<ImageStyle>) {
  const style = useStyle<ImageStyle>(rest);
  const src = useMemo(() => {
    const images = getImages();

    if (name) {
      const [ns, img] = name.split('.');

      return images[ns as keyof RegisteredImages]?.[
        img as keyof RegisteredImages[keyof RegisteredImages]
      ];
    }

    if (typeof source === 'string') {
      return { uri: source };
    }

    return source;
  }, [name, source]);

  return (
    <Image
      style={style}
      source={src}
      placeholder={placeholder}
      contentFit={contentFit}
      placeholderContentFit={placeholderContentFit}
      contentPosition={contentPosition}
      transition={transition}
      blurRadius={blurRadius}
      tintColor={tintColor}
      priority={priority}
      cachePolicy={cachePolicy}
      responsivePolicy={responsivePolicy}
      recyclingKey={recyclingKey}
      autoplay={autoplay}
      onLoadStart={maybeDefer(onLoadStart)}
      onLoad={maybeDefer(onLoad)}
      onProgress={maybeDefer(onProgress)}
      onError={maybeDefer(onError)}
      onLoadEnd={maybeDefer(onLoadEnd)}
      onDisplay={maybeDefer(onDisplay)}
      focusable={focusable}
      accessible={accessible}
      accessibilityLabel={accessibilityLabel}
      alt={alt}
      enableLiveTextInteraction={enableLiveTextInteraction}
      allowDownscaling={allowDownscaling}
      decodeFormat={decodeFormat}
      testID={testID}
    />
  );
}

CustomImage.prefetch = Image.prefetch;
CustomImage.loadAsync = Image.loadAsync;

export default CustomImage;
